/**
 * The devices of a site a remote session may open (design §6): the rows of
 * `GET network-targets/?site=` (the Troubleshooter picker's list, admin's
 * `list_network_targets`) whose source is a camera, the VMS/NVR server, or
 * the management, event or recording servers. Tunnel and IPsec ranges are
 * not offered. The request names the row (`source`, `ref`), never an address.
 */
import { createApi } from "../../../utils/createApi";
import { withSessionRecovery } from "../../Troubleshooting/TroubleshootingUtils/authRecovery";
import {
  DEVICE_SESSION_SOURCES,
  DeviceSessionSource,
  DeviceSessionTarget,
} from "./deviceSessionTypes";

export const SOURCE_LABEL: Record<DeviceSessionSource, string> = {
  camera: "Camera",
  server: "VMS / NVR server",
  management_server: "Management server",
  event_server: "Event server",
  recording_server: "Recording server",
};

/** Order in the picker: site servers first, then recording servers, cameras. */
const SOURCE_ORDER: Record<DeviceSessionSource, number> = {
  server: 0,
  management_server: 1,
  event_server: 2,
  recording_server: 3,
  camera: 4,
};

interface ApiTargetRow {
  address?: string | null;
  label?: string | null;
  site_id?: number | null;
  camera_id?: number | null;
  source?: string | null;
  /** When admin names the row's ref itself. */
  ref?: string | number | null;
  /** A recording_server row's RecordingServer pk. */
  recording_server_id?: number | null;
}

const isSessionSource = (value: unknown): value is DeviceSessionSource =>
  DEVICE_SESSION_SOURCES.includes(value as DeviceSessionSource);

/**
 * The `ref` admin resolves a row by: the camera id, the RecordingServer pk,
 * or "" for the site's own server fields. Null when the row doesn't say
 * (an older admin's recording_server rows carry no id): not offered.
 */
export const refForRow = (row: ApiTargetRow): string | null => {
  if (row.ref != null && row.ref !== "") return String(row.ref);
  switch (row.source) {
    case "camera":
      return row.camera_id != null ? String(row.camera_id) : null;
    case "recording_server":
      return row.recording_server_id != null
        ? String(row.recording_server_id)
        : null;
    case "server":
    case "management_server":
    case "event_server":
      return "";
    default:
      return null;
  }
};

/** The rows a session may open, one per (source, ref), in picker order. */
export const deviceTargetsFromRows = (
  rows: ApiTargetRow[],
  siteId: number
): DeviceSessionTarget[] => {
  const seen = new Set<string>();
  const out: DeviceSessionTarget[] = [];
  for (const row of rows) {
    if (!isSessionSource(row.source)) continue;
    if (row.site_id != null && row.site_id !== siteId) continue;
    const ref = refForRow(row);
    if (ref == null) continue;
    const key = `${row.source}:${ref}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      siteId,
      source: row.source,
      ref,
      label: row.label || row.address || SOURCE_LABEL[row.source],
      address: row.address ?? null,
    });
  }
  return out.sort(
    (a, b) =>
      SOURCE_ORDER[a.source] - SOURCE_ORDER[b.source] ||
      a.label.localeCompare(b.label)
  );
};

export const fetchSiteDeviceTargets = async (
  siteId: number
): Promise<DeviceSessionTarget[]> => {
  const response = await withSessionRecovery(() =>
    createApi("").get<{ results?: ApiTargetRow[] } | ApiTargetRow[]>(
      `network-targets/?site=${siteId}&limit=200`
    )
  );
  const rows = Array.isArray(response.data)
    ? response.data
    : response.data?.results ?? [];
  return deviceTargetsFromRows(rows, siteId);
};

/** A camera on its own page: the camera row, preselected. */
export const cameraTarget = (
  siteId: number,
  cameraId: number,
  cameraName?: string | null
): DeviceSessionTarget => ({
  siteId,
  source: "camera",
  ref: String(cameraId),
  label: cameraName || `Camera ${cameraId}`,
});
